/**
 * PokeShani plan configuration.
 *
 * Single source of truth for the free-plan revenue cap, the plan tiers and
 * the payment instructions shown on the pricing page / upgrade modal.
 * Change the numbers here and the whole app follows.
 */

export type PlanId = 'free' | 'pro' | 'lifetime';

export interface PlanDefinition {
  id: PlanId;
  name: string;
  /** null = nothing to pay */
  priceUsd: number | null;
  billingLabel: string;
  tagline: string;
  /** null = unlimited revenue */
  revenueLimit: number | null;
  features: string[];
  cta: string;
  highlight?: boolean;
}

/**
 * Lifetime revenue cap for the free plan, per creator, summed across all of
 * their products. Measured with `creator_analytics.total_revenue`.
 */
export const FREE_REVENUE_LIMIT = 250;

export const PLANS: PlanDefinition[] = [
  {
    id: 'free',
    name: 'Free',
    priceUsd: null,
    billingLabel: 'forever',
    tagline: 'Everything you need to start selling.',
    revenueLimit: FREE_REVENUE_LIMIT,
    features: [
      'Unlimited digital products',
      'Unlimited sales pages',
      '0% platform fees, you pay PayPal only',
      'One-time, subscription and free products',
      'Sales and revenue analytics',
      `Up to $${FREE_REVENUE_LIMIT} revenue`,
    ],
    cta: 'Current plan',
  },
  {
    id: 'pro',
    name: 'Pro',
    priceUsd: 9,
    billingLabel: 'per month',
    tagline: 'Unlimited selling, no cap.',
    revenueLimit: null,
    highlight: true,
    features: [
      'Everything in Free',
      'Unlimited revenue, no lifetime cap',
      'Checkout never pauses',
      'Remove the "Made with PokeShani" badge',
      'Priority support',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    priceUsd: 99,
    billingLabel: 'one-time',
    tagline: 'Pay once, sell forever.',
    revenueLimit: null,
    features: [
      'Everything in Pro',
      'Lifetime access, never expires',
      'Every future Pro feature included',
      'Best value for power sellers',
    ],
    cta: 'Get Lifetime',
  },
];

export const getPlan = (id: PlanId): PlanDefinition =>
  PLANS.find((p) => p.id === id) || PLANS[0];

/** Support / upgrade contact. */
export const CONTACT_EMAIL = 'official@pokeshani.com';

export interface PaymentMethod {
  id: string;
  label: string;
  /** Receiving address or bank details. */
  address: string;
  note: string;
}

/**
 * Payment rail used to activate a plan. PokeShani never holds customer funds
 * through a processor — each Upgrade click mints a unique one-time deposit
 * address (a forwarder owned by our factory contract) that expires after 10
 * minutes. The backend watches that address, auto-activates on payment, and
 * sweeps the USDC to the main beneficiary below. The legacy tx-hash paste
 * flow (POST /api/pro/verify-payment) stays as a manual fallback.
 */
export const USDC_POLYGON_CONTRACT = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';

export const PRO_CHAIN = {
  id: 137,
  name: 'Polygon',
  currency: 'USDC',
  explorerTxUrl: 'https://polygonscan.com/tx/',
  explorerAddressUrl: 'https://polygonscan.com/address/',
} as const;

export const PRO_RECEIVING_ADDRESS = '0xCb97E29e75782a356665e559AF2811e6258A2fcE';

export const PAYMENT_METHODS: PaymentMethod[] = [
  {
    id: 'usdc-polygon',
    label: 'USDC (Polygon)',
    address: PRO_RECEIVING_ADDRESS,
    note: 'Only send USDC on the Polygon network to this address.',
  },
];

/**
 * When the public sales page cannot read a creator's plan status (for example
 * the quota RPC has not been deployed yet) we let the sale through rather than
 * breaking every sales page. Flip to false once the RPC is live if you would
 * rather fail closed.
 */
export const FAIL_OPEN_WHEN_UNKNOWN = true;